import { ConnectionPositionPair } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { debouncedSearch } from '@vsolv/utils/rxjs-operators';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import {
  BadgeColumn,
  MultiTagColumn,
  PaginationConfig,
  SortableTableComponent,
  SortParams,
  TableColumn,
  TextColumn,
} from '@vsolv/vectors/table';
import { Customer } from '@wsphere/customers/domain';
import { CustomerService } from '@wsphere/customers/web';
import { Distributor } from '@wsphere/distributors/domain';
import { SecurityService } from '@wsphere/staff/web';
import { Claim, ClaimLifecycleStep, Warranty } from '@wsphere/warranties/domain';
import moment from 'moment';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  from,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { FeatureFlagsService, NEW_CLAIMS_FLOW_FLAG } from '../../../policy/services/feature-flags.service';
import { CLAIM_QUEUE_CONFIG, HoldDueOption } from '../../config/claim-queue.config';
import { OpenClaimDialog } from '../../dialogs';
import { ClaimResolutionOptionsService, ClaimService } from '../../services';

export type ClaimsTableFilters = Pick<
  Claim.ListClaimsQueryRequest,
  | 'search'
  | 'statuses'
  | 'claimItemStatuses'
  | 'claimPaymentStatuses'
  | 'claimLifecycleSteps'
  | 'resolutions'
  | 'customerId'
  | 'distributorId'
  | 'warrantyId'
  | 'minAgeDays'
  | 'hasOpenHold'
  | 'hasOpenEscalation'
  | 'authorizationStates'
  | 'paymentReadiness'
  | 'contactReasons'
  | 'overrideStates'
> & {
  // Stored as the option, so saved links stay relative to today.
  holdDue?: HoldDueOption;
  repairDue?: HoldDueOption;
};

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claims-table',
  templateUrl: './claims-table.component.html',
  standalone: false,
})
export class ClaimsTableComponent extends SortableTableComponent implements OnInit, OnDestroy {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private claimsSvc: ClaimService,
    private claimResolutionOptionsSvc: ClaimResolutionOptionsService,
    private securitySvc: SecurityService,
    private customerSvc: CustomerService,
    private featureFlagsSvc: FeatureFlagsService
  ) {
    super();
  }

  @ViewChild('openClaimDialog') openClaimDialog?: OpenClaimDialog;

  @PropertyListener('warranty') private warranty$ = new BehaviorSubject<Warranty.Model | null>(null);
  @Input() warranty: Warranty.Model | null = null;

  @PropertyListener('hideCustomer') private hideCustomer$ = new BehaviorSubject<boolean>(false);
  @Input() hideCustomer?: boolean;

  @PropertyListener('isCustomerPortal') private isCustomerPortal$ = new BehaviorSubject<boolean>(false);
  @Input() isCustomerPortal?: boolean;

  @Input() customerId?: string;

  @Input() hideBorder?: boolean;
  @Input() hideFilters?: boolean;
  @Input() hidePagination?: boolean;

  @Input() isPastWaitingPeriod!: boolean | null;
  @Input() remainingPeriod!: number | null;
  @Input() period!: string | null;

  positions = [
    new ConnectionPositionPair({ originX: 'end', originY: 'bottom' }, { overlayX: 'end', overlayY: 'top' }),
    new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'start', overlayY: 'top' }),
  ];

  open = Claim.Status.OPEN;
  active = Warranty.Status.ACTIVATED;

  statuses = Object.keys(Claim.Status);
  itemStatuses = Object.keys(Claim.ClaimItem.Status);
  paymentStatuses = Object.keys(Claim.ClaimPayment.Status);
  unspecifiedResolutionValue = Claim.CLAIM_RESOLUTION_UNSPECIFIED_FILTER_VALUE;

  readonly queueConfig = CLAIM_QUEUE_CONFIG;
  authorizationStates = Object.values(Claim.Authorization.State);
  paymentReadinessValues = Object.values(Claim.PaymentReadiness);
  contactReasonValues = Object.values(Claim.ContactReason);
  approvalStates = Object.values(Claim.Override.State);

  distributors: Distributor.Model[] = [];
  filters: ClaimsTableFilters | null = null;
  selectedCustomer: Customer.Model | null = null;
  selectedDistributor: Distributor.Model | null = null;
  searchInputValue = '';
  filtersOverlayOpen = false;

  get activeFilterCount(): number {
    if (!this.filters) return 0;

    let count = 0;
    if (this.filters.statuses?.length) count++;
    if (this.filters.resolutions?.length) count++;
    if (this.filters.claimItemStatuses?.length) count++;
    if (this.filters.claimPaymentStatuses?.length) count++;
    if (this.filters.claimLifecycleSteps?.length) count++;
    if (this.filters.distributorId) count++;
    if (this.filters.customerId) count++;
    if (this.filters.minAgeDays !== undefined) count++;
    if (this.filters.hasOpenHold) count++;
    if (this.filters.holdDue) count++;
    if (this.filters.hasOpenEscalation) count++;
    if (this.filters.authorizationStates?.length) count++;
    if (this.filters.paymentReadiness?.length) count++;
    if (this.filters.contactReasons?.length) count++;
    if (this.filters.overrideStates?.length) count++;
    if (this.filters.repairDue) count++;

    return count;
  }

  private onDestroy$ = new Subject<void>();

  private filters$ = new BehaviorSubject(this.filters);
  private refresh$ = new BehaviorSubject<null>(null);
  readonly searchInput$ = new Subject<string>();

  protected lifeCycleSteps$ = this.refresh$.pipe(switchMap(() => this.claimsSvc.lifecycleStepsFilter()));
  protected resolutionOptions$ = from(this.claimResolutionOptionsSvc.listOptions()).pipe(
    catchError(() => of<string[]>([])),
    shareReplay(1)
  );

  override onSortChanged(sortData: SortParams) {
    super.onSortChanged(sortData);
    this.paginationQueryRequest$.next({ ...this.paginationQueryRequest$.value, page: 1 });
  }

  readonly pagination$ = new BehaviorSubject({ currentPage: 1, itemsPerPage: 10 });
  paginationQueryRequest$ = new BehaviorSubject<{ page?: number; limit?: number }>({ page: 1, limit: 10 });

  claims$ = combineLatest([
    this.paginationQueryRequest$,
    this.filters$,
    this.warranty$,
    this.securitySvc.globalDistributors$,
    this.refresh$,
  ]).pipe(
    switchMap(([pagination, filters, warranty]) => {
      const sort = this.sort$.value;
      return this.claimsSvc.list$({
        ...pagination,
        search: filters?.search,
        statuses: filters?.statuses,
        resolutions: filters?.resolutions,
        distributorId: filters?.distributorId,
        claimItemStatuses: filters?.claimItemStatuses,
        claimLifecycleSteps: filters?.claimLifecycleSteps,
        claimPaymentStatuses: filters?.claimPaymentStatuses,
        customerId: this.customerId ? this.customerId : filters?.customerId,
        warrantyId: filters?.warrantyId || warranty?.id,
        minAgeDays: filters?.minAgeDays,
        hasOpenHold: filters?.hasOpenHold,
        holdDueBy: this.dueBy(filters?.holdDue),
        hasOpenEscalation: filters?.hasOpenEscalation,
        authorizationStates: filters?.authorizationStates,
        paymentReadiness: filters?.paymentReadiness,
        contactReasons: filters?.contactReasons,
        overrideStates: filters?.overrideStates,
        repairDeadlineBy: this.dueBy(filters?.repairDue),
        ...(sort && { sortBy: sort.sortBy, sortOrder: sort.sortOrder }),
      });
    }),
    tap(data =>
      this.distributors.length === 0
        ? (this.distributors = data?.items?.reduce((acc, claim) => {
            if (
              claim.warranty?.distributor &&
              !acc.map(distributor => distributor.id).includes(claim.warranty.distributor.id || '')
            ) {
              acc.push(claim.warranty.distributor);
            }
            return acc;
          }, [] as Distributor.Model[]))
        : 0
    ),
    shareReplay(1)
  );

  paginationConfig$ = this.claims$.pipe(
    map((claims: Claim.ListClaimsQueryResponse) => {
      const paginationConfig: PaginationConfig = {
        itemsPerPage: claims?.meta?.itemsPerPage,
        currentPage: claims?.meta?.currentPage,
        pageSizes: [10, 20, 50],
        totalItems: claims?.meta?.totalItems || 0,
      };

      return paginationConfig;
    })
  );

  // The queue shows while the tenant's flag is on or any claim still runs the new flow; null until the flags load.
  protected queueEnabled$: Observable<boolean | null> = combineLatest([
    this.featureFlagsSvc.isEnabledState(NEW_CLAIMS_FLOW_FLAG),
    this.isCustomerPortal$,
  ]).pipe(
    switchMap(async ([flag, isCustomerPortal]) => {
      if (isCustomerPortal) return false;
      if (flag === null) return null;
      return flag || (await this.hasNewFlowClaims());
    }),
    distinctUntilChanged(),
    shareReplay(1)
  );

  columns$: Observable<TableColumn<unknown>[]> = combineLatest([
    this.hideCustomer$,
    this.isCustomerPortal$,
    this.queueEnabled$,
  ]).pipe(
    map(([hideCustomer, isCustomerPortal, queueEnabled]) => {
      return [
        new TextColumn({ header: 'Opened ', fitContent: true, sortable: true }, (item: Claim.Model) => ({
          text: moment(item.created).format('MM/DD/YY'),
          classes: 'whitespace-nowrap',
          tooltip: {
            title: 'Opened',
            subtitle: `
              <p class="font-normal max-w-md truncate -mt-1">${moment(item.created).format(
                'MMM DD, YYYY hh:mm:ss A'
              )}</p>
            `,
          },
        })),

        ...(!hideCustomer
          ? [
              new TextColumn({ header: 'Customer', fitContent: true }, (item: Claim.Model) => {
                const name = item.warranty?.customer?.name || 'Unknown';
                return {
                  text: name,
                  description: item.warranty?.customer?.email || undefined,
                  classes: `truncate max-w-xs ${item.warranty?.customer?.name ? '' : 'italic'}`,
                  copyText: item.warranty?.customer?.name || null,
                  tooltip: item.warranty?.customer?.name
                    ? {
                        title: 'Customer',
                        subtitle: `
                          <p class="font-normal max-w-md truncate -mt-1">${item.warranty.customer.name}</p>
                          ${
                            item.warranty.customer.email &&
                            `<p class="font-normal max-w-md truncate">${item.warranty.customer.email}</p>`
                          }
                        `,
                      }
                    : undefined,
                };
              }),
            ]
          : []),

        new TextColumn({ header: 'Policy', fitContent: true, sortable: true }, (item: Claim.Model) => {
          return {
            text: item?.warranty?.policy?.title || 'Unknown',
            description: item?.warranty?.plan?.title || undefined,
            classes: 'truncate max-w-xs',
            tooltip: {
              title: 'Policy',
              subtitle: `
                <p class="font-normal max-w-md truncate -mt-1">${item?.warranty?.policy?.title || 'Unknown'}</p>
                ${
                  item?.warranty?.policy
                    ? `<p class="font-normal max-w-md truncate">${item.warranty.policy.policyNumber}</p>`
                    : ''
                }

                <p class="font-semibold text-gray-700 mt-2">Plan</p>
                <p class="font-normal max-w-md truncate">${item?.warranty?.plan?.title || 'Unknown'}</p>

              `,
            },
          };
        }),

        new BadgeColumn({ header: 'Status', fitContent: true, sortable: true }, (item: Claim.Model) => {
          const badge = this.getBadgeStatusColor(item.status);
          return { text: badge.text, theme: badge.color, displayStatusIcon: true };
        }),

        ...(!isCustomerPortal && queueEnabled === true ? this.queueColumns() : []),

        new TextColumn({ header: 'Resolution', fitContent: true, sortable: true }, (item: Claim.Model) => ({
          text: item.resolution ?? 'Unspecified',
          classes: item.resolution ? 'truncate max-w-xs' : 'italic text-gray-500',
        })),

        new BadgeColumn({ header: 'Lifecycle', fitContent: true }, (item: Claim.Model) => {
          const step = this.getCurrentStep(item.claimLifecycleSteps);
          return {
            theme: this.getStepTheme(step?.status),
            text: step?.title,
            displayStatusIcon: true,
          };
        }),

        new MultiTagColumn<Claim.Model>({ header: 'Item(s)', fitContent: true }, async claim => {
          if (!claim || !claim.items) return { items: [] };

          return {
            limit: 1,
            classes: claim.items?.length ? 'w-[256px] max-w-[256px]' : '',
            items: claim.items.map(item => ({
              text: item.coverage?.title || 'Other',
              theme: this.getClaimItemStatusColor(item.status),
              displayStatusIcon: true,
            })),
          };
        }),

        ...(!isCustomerPortal
          ? [
              new MultiTagColumn<Claim.Model>({ header: 'Payments', fitContent: true }, async claim => {
                const payments = await this.claimsSvc.listPayments(claim.id);
                if (!payments || !payments.items?.length) return { items: [] };

                return {
                  limit: 1,
                  items: payments.items.map(payment => ({
                    text:
                      payment.status.substring(0, 1) +
                      payment.status.substring(1).toLowerCase() +
                      ' $' +
                      (payment.amount / 100).toFixed(2),
                    theme: this.getPaymentTheme(payment.status),
                    displayStatusIcon: true,
                  })),
                };
              }),
            ]
          : []),

        new TextColumn({ header: 'Distributor', fitContent: true }, (item: Claim.Model) => ({
          theme: item.warranty?.distributor ? 'primary' : 'default',
          classes: 'border rounded-md px-2 py-1 bg-base whitespace-nowrap',
          text: item.warranty?.distributor?.name || '-',
        })),
      ];
    })
  );

  /** Attention tags for each row. */
  private queueColumns(): TableColumn<unknown>[] {
    const config = this.queueConfig;

    return [
      new MultiTagColumn<Claim.Model>({ header: config.columns.attention, fitContent: true }, async claim => {
        const queue = claim.queue;
        if (!queue) return { items: [] };

        const items: { text: string; theme: ThemeColor; displayStatusIcon: boolean }[] = [];
        if (this.isHoldOverdue(queue))
          items.push({ text: config.tags.overdue, theme: 'danger', displayStatusIcon: true });
        if (queue.openHolds)
          items.push({ text: config.tags.holds(queue.openHolds), theme: 'warn', displayStatusIcon: true });
        if (queue.openEscalations) {
          items.push({ text: config.tags.escalations(queue.openEscalations), theme: 'warn', displayStatusIcon: true });
        }
        if (claim.contactReason) {
          const reason = config.contactReason.labels[claim.contactReason];
          items.push({ text: config.tags.contact(reason), theme: 'info', displayStatusIcon: true });
        }
        if (this.isRepairOverdue(claim)) {
          items.push({ text: config.tags.repairOverdue, theme: 'danger', displayStatusIcon: true });
        }
        if (queue.pendingApprovals) {
          items.push({ text: config.tags.approvals(queue.pendingApprovals), theme: 'warn', displayStatusIcon: true });
        }
        if (queue.paymentReadiness === Claim.PaymentReadiness.HELD_BACK) {
          items.push({ text: config.paymentReadiness.labels.HELD_BACK, theme: 'warn', displayStatusIcon: true });
        }
        if (queue.paymentReadiness === Claim.PaymentReadiness.READY) {
          items.push({ text: config.paymentReadiness.labels.READY, theme: 'success', displayStatusIcon: true });
        }
        if (queue.paymentReadiness === Claim.PaymentReadiness.IN_PROGRESS) {
          items.push({ text: config.paymentReadiness.labels.IN_PROGRESS, theme: 'info', displayStatusIcon: true });
        }

        return { limit: 2, items };
      }),

      new TextColumn({ header: config.columns.holdDue, fitContent: true, sortable: true }, (item: Claim.Model) => {
        const due = item.queue?.nextHoldDueDate;
        return {
          text: due ? moment(due).format('MM/DD/YY') : config.columns.none,
          classes: `whitespace-nowrap ${item.queue && this.isHoldOverdue(item.queue) ? 'text-danger-600' : ''}`,
        };
      }),

      new TextColumn(
        { header: config.columns.repairDeadline, fitContent: true, sortable: true },
        (item: Claim.Model) => ({
          // A calendar day, stored as that day in UTC.
          text: item.repairDeadline ? moment.utc(item.repairDeadline).format('MM/DD/YY') : config.columns.none,
          classes: `whitespace-nowrap ${this.isRepairOverdue(item) ? 'text-danger-600' : ''}`,
        })
      ),
    ];
  }

  private isHoldOverdue(queue: Claim.QueueSummary) {
    return !!queue.nextHoldDueDate && moment(queue.nextHoldDueDate).isBefore(moment(), 'day');
  }

  private isRepairOverdue(claim: Claim.Model) {
    return (
      !!claim.repairDeadline && moment.utc(claim.repairDeadline).format('YYYY-MM-DD') < moment().format('YYYY-MM-DD')
    );
  }

  /** Last due day that matches the option. */
  private dueBy(option?: HoldDueOption) {
    const days = this.queueConfig.holdDue.options.find(entry => entry.value === option)?.days;
    return days === undefined ? undefined : moment().add(days, 'days').format('YYYY-MM-DD');
  }

  async openOpenClaimDialog(warranty?: Warranty.Model | null, customer?: Customer.Model | null) {
    const warrantyToUse = warranty ?? this.warranty ?? null;

    if (!warrantyToUse) {
      this.openClaimDialog?.openDialog();
      return;
    }

    let customerToUse = customer ?? warrantyToUse.customer ?? null;

    if (!customerToUse && warrantyToUse.customerId) {
      try {
        customerToUse = await this.customerSvc.retrieveCustomer(warrantyToUse.customerId);
      } catch {
        customerToUse = null;
      }
    }

    const warrantyWithCustomer =
      customerToUse && !warrantyToUse.customer ? { ...warrantyToUse, customer: customerToUse } : warrantyToUse;

    this.openClaimDialog?.openDialog(warrantyWithCustomer, customerToUse ?? null);
  }

  closeClaimDialog() {
    this.refresh$.next(null);
  }

  private getCurrentStep(steps?: ClaimLifecycleStep.Model[]) {
    return steps?.find(
      step =>
        (this.isCustomerPortal ? step.visible === true : true) &&
        step.status !== ClaimLifecycleStep.Status.DONE &&
        step.status !== ClaimLifecycleStep.Status.SKIPPED
    );
  }

  clearWarrantyIdFilter() {
    this.filters = {
      ...this.filters,
      warrantyId: undefined,
    };
    this.filters$.next(this.filters);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { warrantyId: null },
      queryParamsHandling: 'merge',
    });
  }

  clearAllFilters() {
    this.selectedCustomer = null;
    this.selectedDistributor = null;
    this.filters = {
      ...this.filters,
      statuses: undefined,
      resolutions: undefined,
      claimItemStatuses: undefined,
      claimPaymentStatuses: undefined,
      claimLifecycleSteps: undefined,
      distributorId: undefined,
      customerId: undefined,
      minAgeDays: undefined,
      hasOpenHold: undefined,
      holdDue: undefined,
      repairDue: undefined,
      contactReasons: undefined,
      overrideStates: undefined,
      hasOpenEscalation: undefined,
      authorizationStates: undefined,
      paymentReadiness: undefined,
    };

    this.filters$.next(this.filters);
    if (!this.isCustomerPortal && !this.warranty) this.syncFiltersToQueryParams();
  }

  private clearQueueFilters() {
    const f = this.filters;
    const hasQueueFilter =
      !!f &&
      !!(
        f.minAgeDays ||
        f.hasOpenHold ||
        f.holdDue ||
        f.repairDue ||
        f.contactReasons?.length ||
        f.overrideStates?.length ||
        f.hasOpenEscalation ||
        f.authorizationStates?.length ||
        f.paymentReadiness?.length
      );
    if (!hasQueueFilter) return;

    this.filters = {
      ...f,
      minAgeDays: undefined,
      hasOpenHold: undefined,
      holdDue: undefined,
      repairDue: undefined,
      contactReasons: undefined,
      overrideStates: undefined,
      hasOpenEscalation: undefined,
      authorizationStates: undefined,
      paymentReadiness: undefined,
    };
    this.filters$.next(this.filters);
    if (!this.warranty) this.syncFiltersToQueryParams();
  }

  /** Any claim in the new flow, open or closed. */
  private async hasNewFlowClaims() {
    const authorizationStates = Object.values(Claim.Authorization.State).filter(
      state => state !== Claim.Authorization.State.NOT_REQUIRED
    );
    try {
      const result = await firstValueFrom(this.claimsSvc.list$({ page: 1, limit: 1, authorizationStates }));
      return (result?.meta?.totalItems ?? 0) > 0;
    } catch {
      return false;
    }
  }

  toggleFiltersOverlay() {
    this.filtersOverlayOpen = !this.filtersOverlayOpen;
  }

  closeFiltersOverlay() {
    this.filtersOverlayOpen = false;
  }

  private getStepTheme(status?: ClaimLifecycleStep.Status): ThemeColor {
    switch (status) {
      case ClaimLifecycleStep.Status.DONE:
        return 'success';
      case ClaimLifecycleStep.Status.IN_PROGRESS:
        return 'info';
      case ClaimLifecycleStep.Status.PENDING_CUSTOMER:
        return 'warn';
      case ClaimLifecycleStep.Status.UNSUCCESSFUL:
        return 'danger';
      default:
        return 'default';
    }
  }

  getBadgeStatusColor(status: Claim.Status): {
    color: ThemeColor;
    text: string;
    tooltip: string;
  } {
    switch (status) {
      case Claim.Status.CANCELLED:
        return {
          text: 'Canceled',
          color: 'warn',
          tooltip: 'Your claim was canceled.',
        };

      case Claim.Status.CLOSED:
        return {
          text: 'Closed',
          color: 'default',
          tooltip: 'Your claim was automatically closed after being successfully reimbursed.',
        };

      case Claim.Status.EXPIRED:
        return {
          text: 'Expired',
          color: 'danger',
          tooltip:
            'Your claim has expired after 30 days of inactivity. If you are still experiencing an issue, please open a new claim.',
        };

      default:
        return {
          text: 'Open',
          color: 'info',
          tooltip: 'Your claim has been received and is currently being processed for approval',
        };
    }
  }

  getClaimItemStatusColor(status: Claim.ClaimItem.Status): ThemeColor {
    switch (status) {
      case Claim.ClaimItem.Status.RESOLVED:
      case Claim.ClaimItem.Status.APPROVED:
        return 'success';
      case Claim.ClaimItem.Status.REJECTED:
        return 'danger';
      default:
        return 'default';
    }
  }

  getPaymentTheme(status: Claim.ClaimPayment.Status): ThemeColor {
    switch (status) {
      case Claim.ClaimPayment.Status.COMPLETED:
        return 'success';
      case Claim.ClaimPayment.Status.FAILED:
        return 'danger';
      case Claim.ClaimPayment.Status.ISSUED:
        return 'info';
      case Claim.ClaimPayment.Status.VOIDED:
      default:
        return 'default';
    }
  }

  navigateTo(claimId: string) {
    if (this.isCustomerPortal) {
      if (this.warranty?.policy?.customerPortalConfig.selfServeClaims) {
        this.router.navigate([`claim/${claimId}`], { relativeTo: this.route });
      }
    } else {
      if (this.warranty) this.router.navigate([`../../claims/${claimId}`], { relativeTo: this.route });
      else this.router.navigate([`${claimId}`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
    }
  }

  private normalizeMultiSelectValue<T>(value: T | readonly T[] | null | undefined): T[] | undefined {
    if (value === undefined) return undefined;
    if (value === null) return [];
    if (Array.isArray(value)) return [...value] as T[];
    return [value as T];
  }

  async refreshTable(filters?: {
    search?: string;
    statuses?: Claim.Status | readonly Claim.Status[] | null;
    resolutions?: string | readonly string[] | null;
    itemStatuses?: Claim.ClaimItem.Status | readonly Claim.ClaimItem.Status[] | null;
    paymentStatuses?: Claim.ClaimPayment.Status | readonly Claim.ClaimPayment.Status[] | null;
    customer?: Customer.Model | null;
    distributor?: Distributor.Model | null;
    claimLifecycleSteps?: string | readonly string[] | null;
    warrantyId?: string;
    minAgeDays?: number | null;
    hasOpenHold?: boolean;
    holdDue?: HoldDueOption | null;
    repairDue?: HoldDueOption | null;
    contactReasons?: Claim.ContactReason | readonly Claim.ContactReason[] | null;
    overrideStates?: Claim.Override.State | readonly Claim.Override.State[] | null;
    hasOpenEscalation?: boolean;
    authorizationStates?: Claim.Authorization.State | readonly Claim.Authorization.State[] | null;
    paymentReadiness?: Claim.PaymentReadiness | readonly Claim.PaymentReadiness[] | null;
  }) {
    if (filters?.search !== undefined) {
      this.searchInput$.next(filters.search);
      return;
    }
    if (filters?.statuses !== undefined) {
      this.filters = {
        ...this.filters,
        statuses: this.normalizeMultiSelectValue<Claim.Status>(filters.statuses),
      };
    }
    if (filters?.resolutions !== undefined) {
      this.filters = {
        ...this.filters,
        resolutions: this.normalizeMultiSelectValue<string>(filters.resolutions),
      };
    }
    if (filters?.itemStatuses !== undefined) {
      this.filters = {
        ...this.filters,
        claimItemStatuses: this.normalizeMultiSelectValue<Claim.ClaimItem.Status>(filters.itemStatuses),
      };
    }
    if (filters?.paymentStatuses !== undefined) {
      this.filters = {
        ...this.filters,
        claimPaymentStatuses: this.normalizeMultiSelectValue<Claim.ClaimPayment.Status>(filters.paymentStatuses),
      };
    }
    if (filters?.customer !== undefined) {
      this.selectedCustomer = filters.customer;
      this.filters = { ...this.filters, customerId: filters.customer?.id };
    }
    if (filters?.distributor !== undefined) {
      this.selectedDistributor = filters.distributor;
      this.filters = { ...this.filters, distributorId: filters.distributor?.id };
    }

    if (filters?.claimLifecycleSteps !== undefined) {
      this.filters = {
        ...this.filters,
        claimLifecycleSteps: this.normalizeMultiSelectValue<string>(filters.claimLifecycleSteps),
      };
    }

    if (filters?.warrantyId !== undefined) {
      this.filters = { ...this.filters, warrantyId: filters.warrantyId };
    }

    // An unchecked box or empty pick clears the filter.
    if (filters?.minAgeDays !== undefined) {
      this.filters = { ...this.filters, minAgeDays: filters.minAgeDays ?? undefined };
    }
    if (filters?.hasOpenHold !== undefined) {
      this.filters = { ...this.filters, hasOpenHold: filters.hasOpenHold || undefined };
    }
    if (filters?.holdDue !== undefined) {
      this.filters = { ...this.filters, holdDue: filters.holdDue ?? undefined };
    }
    if (filters?.repairDue !== undefined) {
      this.filters = { ...this.filters, repairDue: filters.repairDue ?? undefined };
    }
    if (filters?.contactReasons !== undefined) {
      this.filters = {
        ...this.filters,
        contactReasons: this.normalizeMultiSelectValue<Claim.ContactReason>(filters.contactReasons),
      };
    }
    if (filters?.overrideStates !== undefined) {
      this.filters = {
        ...this.filters,
        overrideStates: this.normalizeMultiSelectValue<Claim.Override.State>(filters.overrideStates),
      };
    }
    if (filters?.hasOpenEscalation !== undefined) {
      this.filters = { ...this.filters, hasOpenEscalation: filters.hasOpenEscalation || undefined };
    }
    if (filters?.authorizationStates !== undefined) {
      this.filters = {
        ...this.filters,
        authorizationStates: this.normalizeMultiSelectValue<Claim.Authorization.State>(filters.authorizationStates),
      };
    }
    if (filters?.paymentReadiness !== undefined) {
      this.filters = {
        ...this.filters,
        paymentReadiness: this.normalizeMultiSelectValue<Claim.PaymentReadiness>(filters.paymentReadiness),
      };
    }

    this.filters$.next(this.filters);
    if (!this.isCustomerPortal && !this.warranty) this.syncFiltersToQueryParams();
  }

  async ngOnInit() {
    this.searchInput$.pipe(debouncedSearch(), takeUntil(this.onDestroy$)).subscribe(search => {
      this.searchInputValue = search;
      this.filters = { ...this.filters, search };
      this.filters$.next(this.filters);
      if (!this.isCustomerPortal && !this.warranty) this.syncFiltersToQueryParams();
    });

    const queryParams = await firstValueFrom(this.route.queryParams);
    const warrantyId = queryParams['warrantyId'] as string | undefined;

    if (!this.isCustomerPortal) {
      const hasSyncedFilters = !this.warranty && this.hasClaimFilterQueryParams();

      if (hasSyncedFilters) {
        const urlFilters = this.getFiltersFromQueryParams();
        this.filters = {
          ...(urlFilters ?? {}),
          ...(urlFilters?.statuses?.length ? {} : { statuses: [this.open] }),
          ...(warrantyId ? { warrantyId } : {}),
        };
        this.searchInputValue = this.filters?.search ?? '';
        this.filters$.next(this.filters);
        await this.hydrateFiltersFromQueryParams();
      } else {
        this.refreshTable({
          statuses: [this.open],
          ...(warrantyId && { warrantyId }),
        });
      }

      // A saved link can carry queue filters the user can't see or change while the queue is hidden.
      this.queueEnabled$
        .pipe(
          filter(enabled => enabled === false),
          take(1),
          takeUntil(this.onDestroy$)
        )
        .subscribe(() => this.clearQueueFilters());
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private hasClaimFilterQueryParams(): boolean {
    const map = this.route.snapshot.queryParamMap;
    return [
      'statuses',
      'resolutions',
      'itemStatuses',
      'paymentStatuses',
      'lifecycleSteps',
      'customer',
      'distributor',
      'search',
      'minAgeDays',
      'openHold',
      'holdDue',
      'repairDue',
      'contactReasons',
      'overrideStates',
      'openEscalation',
      'authorizationStates',
      'paymentReadiness',
    ].some(key => map.has(key));
  }

  private parseArrayParam(key: string): string[] {
    const values = this.route.snapshot.queryParamMap.getAll(key);
    if (!values.length) {
      const value = this.route.snapshot.queryParamMap.get(key);
      return value
        ? value
            .split(',')
            .map(v => v.trim())
            .filter(Boolean)
        : [];
    }
    return values
      .flatMap(v => v.split(','))
      .map(v => v.trim())
      .filter(Boolean);
  }

  private getFiltersFromQueryParams(): ClaimsTableFilters | null {
    const search = this.route.snapshot.queryParamMap.get('search')?.trim() ?? '';
    const statuses = this.parseArrayParam('statuses').filter((value): value is Claim.Status =>
      this.statuses.includes(value)
    );
    const resolutions = this.parseArrayParam('resolutions');
    const claimItemStatuses = this.parseArrayParam('itemStatuses').filter((value): value is Claim.ClaimItem.Status =>
      this.itemStatuses.includes(value)
    );
    const claimPaymentStatuses = this.parseArrayParam('paymentStatuses').filter(
      (value): value is Claim.ClaimPayment.Status => this.paymentStatuses.includes(value)
    );
    const claimLifecycleSteps = this.parseArrayParam('lifecycleSteps');
    const minAgeDays = Number(this.route.snapshot.queryParamMap.get('minAgeDays'));
    const holdDue = this.queueConfig.holdDue.options.find(
      option => option.value === this.route.snapshot.queryParamMap.get('holdDue')
    )?.value;
    const repairDue = this.queueConfig.holdDue.options.find(
      option => option.value === this.route.snapshot.queryParamMap.get('repairDue')
    )?.value;
    const contactReasons = this.parseArrayParam('contactReasons').filter((value): value is Claim.ContactReason =>
      this.contactReasonValues.includes(value as Claim.ContactReason)
    );
    const overrideStates = this.parseArrayParam('overrideStates').filter((value): value is Claim.Override.State =>
      this.approvalStates.includes(value as Claim.Override.State)
    );
    const authorizationStates = this.parseArrayParam('authorizationStates').filter(
      (value): value is Claim.Authorization.State =>
        this.authorizationStates.includes(value as Claim.Authorization.State)
    );
    const paymentReadiness = this.parseArrayParam('paymentReadiness').filter((value): value is Claim.PaymentReadiness =>
      this.paymentReadinessValues.includes(value as Claim.PaymentReadiness)
    );

    const filters: ClaimsTableFilters = {};
    if (search) filters.search = search;
    if (statuses.length) filters.statuses = statuses;
    if (resolutions.length) filters.resolutions = resolutions;
    if (claimItemStatuses.length) filters.claimItemStatuses = claimItemStatuses;
    if (claimPaymentStatuses.length) filters.claimPaymentStatuses = claimPaymentStatuses;
    if (claimLifecycleSteps.length) filters.claimLifecycleSteps = claimLifecycleSteps;
    if (minAgeDays > 0) filters.minAgeDays = minAgeDays;
    if (this.route.snapshot.queryParamMap.get('openHold') === 'true') filters.hasOpenHold = true;
    if (holdDue) filters.holdDue = holdDue;
    if (this.route.snapshot.queryParamMap.get('openEscalation') === 'true') filters.hasOpenEscalation = true;
    if (authorizationStates.length) filters.authorizationStates = authorizationStates;
    if (paymentReadiness.length) filters.paymentReadiness = paymentReadiness;
    if (repairDue) filters.repairDue = repairDue;
    if (contactReasons.length) filters.contactReasons = contactReasons;
    if (overrideStates.length) filters.overrideStates = overrideStates;

    return Object.keys(filters).length ? filters : null;
  }

  private async hydrateFiltersFromQueryParams(): Promise<void> {
    const customerName = this.route.snapshot.queryParamMap.get('customer')?.trim() ?? null;
    const distributorName = this.route.snapshot.queryParamMap.get('distributor')?.trim() ?? null;

    const [selectedCustomer, selectedDistributor] = await Promise.all([
      this.resolveCustomerFromQueryParam(customerName),
      this.resolveDistributorFromQueryParam(distributorName),
    ]);

    this.selectedCustomer = selectedCustomer;
    this.selectedDistributor = selectedDistributor;

    if (selectedCustomer || selectedDistributor) {
      this.filters = {
        ...(this.filters ?? {}),
        ...(selectedCustomer ? { customerId: selectedCustomer.id } : {}),
        ...(selectedDistributor ? { distributorId: selectedDistributor.id } : {}),
      };
      this.filters$.next(this.filters);
    }
  }

  private async resolveCustomerFromQueryParam(customerName: string | null): Promise<Customer.Model | null> {
    if (!customerName) return null;
    try {
      const response = await this.customerSvc.listCustomers({ search: customerName, page: 1, limit: 50 });
      return response?.items.find(c => c.name.trim().toLowerCase() === customerName.trim().toLowerCase()) ?? null;
    } catch {
      return null;
    }
  }

  private async resolveDistributorFromQueryParam(distributorName: string | null): Promise<Distributor.Model | null> {
    if (!distributorName) return null;
    try {
      const globalDistributors = await firstValueFrom(this.securitySvc.globalDistributors$);
      const permissionKeys = globalDistributors?.map(d => d.permissionKey) ?? null;
      const response = await this.securitySvc.listDistributorsWithPermissionKeys({
        search: distributorName,
        permissionId: 'clm_ViewClaim',
        permissionKeys,
      });
      return (response.items.find(d => d.name.trim().toLowerCase() === distributorName.trim().toLowerCase()) ??
        null) as unknown as Distributor.Model | null;
    } catch {
      return null;
    }
  }

  private syncFiltersToQueryParams(): void {
    const queryParams = {
      search: this.filters?.search || null,
      statuses: this.filters?.statuses?.length ? this.filters.statuses.join(',') : null,
      resolutions: this.filters?.resolutions?.length ? this.filters.resolutions.join(',') : null,
      itemStatuses: this.filters?.claimItemStatuses?.length ? this.filters.claimItemStatuses.join(',') : null,
      paymentStatuses: this.filters?.claimPaymentStatuses?.length ? this.filters.claimPaymentStatuses.join(',') : null,
      lifecycleSteps: this.filters?.claimLifecycleSteps?.length ? this.filters.claimLifecycleSteps.join(',') : null,
      customer: this.selectedCustomer?.name || null,
      distributor: this.selectedDistributor?.name || null,
      minAgeDays: this.filters?.minAgeDays ?? null,
      openHold: this.filters?.hasOpenHold ? 'true' : null,
      holdDue: this.filters?.holdDue ?? null,
      openEscalation: this.filters?.hasOpenEscalation ? 'true' : null,
      authorizationStates: this.filters?.authorizationStates?.length
        ? this.filters.authorizationStates.join(',')
        : null,
      paymentReadiness: this.filters?.paymentReadiness?.length ? this.filters.paymentReadiness.join(',') : null,
      repairDue: this.filters?.repairDue ?? null,
      contactReasons: this.filters?.contactReasons?.length ? this.filters.contactReasons.join(',') : null,
      overrideStates: this.filters?.overrideStates?.length ? this.filters.overrideStates.join(',') : null,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
